// apps/share-web/src/player-view/phone-mode.ts
//
// Живой режим телефона для блока чарта.
//
// Долг, который этот модуль закрывает —
// TD-260828-chart-phone-mode-snapshotted-at-mount. Раньше mount.ts читал
// `isPhoneWidth(window.innerWidth)` ОДИН раз, при монтаже, и жил с этим снимком
// до смены песни. Узкое окно, открытое ДО монтажа, вело себя как телефон;
// сужённое ПОСЛЕ — как десктоп, и кнопка «Chart & Lyrics» раздвигала панель
// шириной 372 px внутри экрана в 400. Ровно это Сергей увидел на приёмке.
//
// Порог берётся из `chart-wiring.ts` и НЕ пишется здесь числом: он же стоит в
// `@media (max-width: 767px)` в player-view.css, где кнопка встаёт под волну
// обзора. Одно число на раскладку и на поведение — иначе кнопка переезжает по
// CSS, а панель всё равно раздвигается по JS, и такое расхождение не падает ни
// одним тестом поведения. Его ловит phone-mode.test.ts сравнением с файлом.
//
// matchMedia спрашивается через `typeof` — так же, как в main.ts и
// audio/background-orchestrator.ts. Отсутствие API это старый мобильный
// браузер, а не «телефона нет»: тогда режим считается по ширине окна, а событием
// служит resize. Ширина в фолбэке читается ЖИВЬЁМ на каждый вопрос, поэтому
// снимок не возвращается через заднюю дверь.

import { PHONE_MAX, isPhoneWidth } from './chart-wiring.js';

/** Медиазапрос режима телефона. Собран из порога, а не написан рядом с ним. */
export const PHONE_MEDIA = `(max-width: ${PHONE_MAX}px)`;

export interface PhoneModeWatch {
  /** Телефон ли СЕЙЧАС. Не снимок: спрашивает окно в момент вызова. */
  isPhone(): boolean;
  /** Отписка. Слушатель сидит на window и переживёт свой монтаж, если её не звать. */
  stop(): void;
}

/**
 * Подписывает на СМЕНУ режима. Колбэк зовётся только при переходе через порог —
 * движение внутри режима молчит: пересобирать блок чарта на каждом пикселе
 * ресайза значит рвать открытое чтение по десять раз за перетаскивание окна.
 */
export function watchPhoneMode(onChange: (phone: boolean) => void): PhoneModeWatch {
  const mql = typeof window.matchMedia === 'function' ? window.matchMedia(PHONE_MEDIA) : null;
  const read = (): boolean => (mql ? mql.matches : isPhoneWidth(window.innerWidth));

  let current = read();
  const onEvent = (): void => {
    const next = read();
    if (next === current) return;
    current = next;
    onChange(next);
  };

  // Слушателя ДВА, и это не перестраховка ради перестраховки.
  //
  // `change` на MediaQueryList — правильный сигнал, но он не единственный
  // надёжный: на iOS Safari известны случаи, когда при повороте он не приходит
  // вовсе, а `resize` приходит всегда. То же расхождение видно и в happy-dom:
  // его MediaQueryList заводит внутреннее `matchesState = false` в момент
  // подписки, не спрашивая текущего состояния, поэтому переход «телефон →
  // широкий» (true → false) он не объявляет — замерено, и ровно на этом падал
  // тест «окно расширили после монтажа».
  //
  // Цена нулевая: обработчик читает одно поле и сравнивает с прошлым, а колбэк
  // зовётся только на переходе через порог — двойного оповещения не бывает,
  // сколько бы событий ни пришло на одно движение окна.
  if (mql) {
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onEvent);
    else mql.addListener(onEvent);
  }
  window.addEventListener('resize', onEvent);

  return {
    isPhone: read,
    stop() {
      if (mql) {
        if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', onEvent);
        else mql.removeListener(onEvent);
      }
      window.removeEventListener('resize', onEvent);
    },
  };
}
